/**
 * W-19 发表评价(BUYER)。视觉真源 design/review.html。
 * 5 星(默认 5)+ 文字(10–500)+ 图片(≤3,presign 直传)+ 二次确认 + POST /reviews。
 * 一单一评、提交即终态不可改。orderId 取自路由。
 */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ServerError } from '@/api/client';
import { REVIEW_ERROR, submitReview, uploadReviewImage } from '@/api/review';
import { toast } from '@/components/ui/toast';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

const CONTENT_MIN = 10;
const CONTENT_MAX = 500;
const MAX_IMAGES = 3;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5MB(与页面提示一致)
const ACCEPT_IMAGE_TYPES = ['image/jpeg', 'image/png'];

const SUBMIT_ERROR_MSG: Record<string, string> = {
  [REVIEW_ERROR.ALREADY_REVIEWED]: '该订单已评价,不可重复提交',
  [REVIEW_ERROR.CONTENT_REJECTED]: '评价内容未通过审核,请修改后重试',
  [REVIEW_ERROR.WINDOW_EXPIRED]: '已超过评价时限',
  [REVIEW_ERROR.RATING_INVALID]: '评分无效',
  [REVIEW_ERROR.CONTENT_LENGTH]: '评价字数需为 10–500 字',
  [REVIEW_ERROR.IMAGE_COUNT]: '最多上传 3 张图片',
  [REVIEW_ERROR.ORDER_NOT_FOUND]: '订单不存在',
  [REVIEW_ERROR.ORDER_NOT_OWNED]: '无权评价该订单',
};

export function ReviewPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const [rating, setRating] = useState(5);
  const [content, setContent] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const contentLen = content.length;
  // 校验用 trim 后长度(提交也发送 trim 值)——避免纯空白凑够 10 字通过前端却被后端 030605 拒。
  const trimmedLen = content.trim().length;
  const contentValid = trimmedLen >= CONTENT_MIN && trimmedLen <= CONTENT_MAX;
  const canSubmit = !!orderId && rating >= 1 && rating <= 5 && contentValid && !submitting && !uploading;

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // 允许重复选同一文件
    if (!file || !orderId) return;
    if (images.length >= MAX_IMAGES) {
      toast.error('最多上传 3 张图片');
      return;
    }
    if (!ACCEPT_IMAGE_TYPES.includes(file.type)) {
      toast.error('仅支持 JPG / PNG 图片');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error('单张图片不超过 5MB');
      return;
    }
    setUploading(true);
    try {
      const url = await uploadReviewImage(orderId, file);
      // 去重:避免同一 finalUrl 重复入列(key 冲突 + 重复提交)。
      setImages((prev) => (prev.includes(url) ? prev : [...prev, url]));
    } catch {
      toast.error('上传失败,请重新选图');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx: number) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const doSubmit = async () => {
    if (!orderId) return;
    setSubmitting(true);
    try {
      await submitReview({ orderId, rating, content: content.trim(), images });
      setConfirmOpen(false);
      toast.success('评价已提交');
      navigate(`/orders/${orderId}`, { replace: true });
    } catch (err) {
      setConfirmOpen(false);
      const code = err instanceof ServerError ? err.code : undefined;
      toast.error(code ? (SUBMIT_ERROR_MSG[code] ?? '提交失败,请稍后重试') : '提交失败,请稍后重试');
      // 已评价不可恢复:引导返回订单详情
      if (code === REVIEW_ERROR.ALREADY_REVIEWED && orderId) {
        navigate(`/orders/${orderId}`, { replace: true });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="inner section">
      <div className="form-narrow" style={{ margin: '0 auto' }}>
        <Link
          to={orderId ? `/orders/${orderId}` : '/orders'}
          className="sm muted"
          style={{ display: 'inline-block', marginBottom: 'var(--space-4)' }}
        >
          ← 返回订单详情
        </Link>

        <h1 className="h2 mb-6">发表评价</h1>

        <section className="card card-pad">
          {/* 星级评分 */}
          <div className="field">
            <label className="label">商品评分</label>
            <div
              className="row gap-2"
              role="radiogroup"
              aria-label="商品评分"
              style={{ fontSize: 28, lineHeight: 1, cursor: 'pointer', userSelect: 'none' }}
            >
              {[1, 2, 3, 4, 5].map((s) => (
                <span
                  key={s}
                  role="radio"
                  aria-checked={rating === s}
                  aria-label={`${s} 星`}
                  tabIndex={0}
                  onClick={() => setRating(s)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setRating(s);
                    }
                  }}
                  style={{ color: s <= rating ? 'var(--color-primary)' : 'var(--color-border)' }}
                >
                  ★
                </span>
              ))}
            </div>
            <p className="hint">点击星标评分,1–5 星(默认 5 星)</p>
          </div>

          {/* 评价正文 */}
          <div className="field">
            <label className="label" htmlFor="review-text">
              评价内容(10–500 字)
            </label>
            <textarea
              className="textarea"
              id="review-text"
              maxLength={CONTENT_MAX}
              placeholder="请描述您的商品体验"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
            <div className="row between">
              <p className="hint">真实、客观的评价有助于其他用户决策</p>
              <p
                className="hint"
                style={contentLen < CONTENT_MIN ? { color: 'var(--color-destructive)' } : undefined}
              >
                {contentLen} / {CONTENT_MAX}
              </p>
            </div>
            {contentLen > 0 && contentLen < CONTENT_MIN && (
              <p className="sm mt-1" role="alert" style={{ color: 'var(--color-destructive)' }}>
                评价内容至少 {CONTENT_MIN} 字才能提交
              </p>
            )}
          </div>

          {/* 图片上传(最多 3 张) */}
          <div className="field">
            <label className="label">上传图片(选填,最多 3 张)</label>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 'var(--space-3)',
                maxWidth: 360,
              }}
            >
              {images.map((url, idx) => (
                <div className="review-thumb" key={url}>
                  <img src={url} alt={`评价图片 ${idx + 1}`} />
                  <button
                    type="button"
                    className="review-thumb-del"
                    aria-label="删除图片"
                    onClick={() => removeImage(idx)}
                  >
                    ×
                  </button>
                </div>
              ))}
              {images.length < MAX_IMAGES && (
                <label className="upload-box">
                  <span>{uploading ? '上传中…' : '+ 添加'}</span>
                  <input type="file" accept="image/*" hidden disabled={uploading} onChange={onPickFile} />
                </label>
              )}
            </div>
            <p className="hint">支持 JPG / PNG,单张不超过 5MB</p>
          </div>

          {/* 提交 */}
          <button
            type="button"
            className="btn btn-primary btn-lg btn-block"
            disabled={!canSubmit}
            onClick={() => setConfirmOpen(true)}
          >
            提交评价
          </button>
          <p className="xs muted mt-4" style={{ textAlign: 'center' }}>
            评价提交后无法修改,请确认后再提交。
          </p>
        </section>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="确认提交评价?"
        description="评价提交后无法修改,是否继续?"
        confirmLabel="确认提交"
        submitting={submitting}
        onConfirm={doSubmit}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
